<template>
  <div class="featured-videos__wrapper" :class="$_ampClass">
    <div class="featured-videos__layout-wrapper" :class="$_ampClass">
      <div class="featured-videos__content-wrapper">
        <a-visibility
          v-if="$helper.isNotAmpPage($route.name)"
          hide
          :to="$breakpoint.desktopMd"
        >
          <h2 :class="$_ampClass" class="featured-videos__title">
            Featured Videos
          </h2>
        </a-visibility>
        <div class="featured-videos__videos-wrapper" :class="$_ampClass">
          <div class="featured-videos__main-video-wrapper">
            <a-video
              :video="mainVideo.Video"
              :aspect-ratio="$options.consts.aspectRatios.homepageTopVideo"
              :rounded="true"
            />
            <a-visibility
              show
              :to="$breakpoint.desktopMd"
              :always-show="$helper.isAmpPage($route.name)"
            >
              <div
                class="featured-videos__main-text-wrapper"
                :class="$_ampClass"
              >
                <a-dots-responsive
                  :text="getVideoTitle(mainVideo)"
                  tag="h4"
                  :settings="$options.consts.mainVideoTitleSettings"
                  :always-mobile="$helper.isAmpPage($route.name)"
                  :class="$_ampClass"
                  class="featured-videos__main-video-title"
                />
                <a-dots-responsive
                  :text="getVideoDescription(mainVideo)"
                  tag="h4"
                  :settings="$options.consts.mainVideoDescriptionSettings"
                  :always-mobile="$helper.isAmpPage($route.name)"
                  :class="$_ampClass"
                  class="featured-videos__main-video-description"
                />
              </div>
            </a-visibility>
          </div>

          <a-visibility
            show
            :on="[$breakpoint.mobile]"
            :always-show="$helper.isAmpPage($route.name)"
          >
            <a-link
              :to="$options.consts.FM_YOUTUBE_URL"
              :open-in-new-tab="true"
              class="featured-videos__more-videos"
            >
              More Videos
            </a-link>
          </a-visibility>

          <ul class="featured-videos__other-videos-wrapper" :class="$_ampClass">
            <li
              v-for="video in otherVideos"
              :key="video.Id"
              :class="$_ampClass"
              class="featured-videos__other-video-wrapper"
            >
              <a-video
                :video="video.Video"
                :aspect-ratio="$options.consts.aspectRatios.homepageTopVideo"
                :rounded="true"
              />
              <div :class="$_ampClass" class="featured-videos__text-wrapper">
                <a-dots-responsive
                  :text="getVideoTitle(video)"
                  tag="h4"
                  :settings="$options.consts.otherVideoTitleSettings"
                  :always-mobile="$helper.isAmpPage($route.name)"
                  :class="$_ampClass"
                  class="featured-videos__other-video-title"
                />
                <a-dots-responsive
                  :text="getVideoDescription(video)"
                  tag="h4"
                  :settings="$options.consts.otherVideoDescriptionSettings"
                  :debug="true"
                  :always-mobile="$helper.isAmpPage($route.name)"
                  :class="$_ampClass"
                  class="featured-videos__other-video-description"
                />
              </div>
            </li>
          </ul>
        </div>
        <a-visibility
          v-if="$helper.isNotAmpPage($route.name)"
          show
          :on="[
            $breakpoint.tablet,
            $breakpoint.desktopSm,
            $breakpoint.desktopMd
          ]"
        >
          <a-button
            :href="$options.consts.FM_YOUTUBE_URL"
            class="featured-videos__see-all-button"
            >See all videos</a-button
          >
        </a-visibility>
      </div>
    </div>
  </div>
</template>
<script>
import { PROP_TYPES, propValidator } from '@/utils/validators'
import mixins from '@/utils/mixins'
import AVideo from 'shared/AVideo'
import { aspectRatios } from 'enums/aspectRatios'
import ADotsResponsive, { configADots } from 'shared/ADotsResponsive'
import { FM_YOUTUBE_URL } from 'enums/youtube'
import scss from '@/utils/scss'
const { breakpoints } = scss

export default {
  name: 'FeaturedVideos',
  mixins: [mixins.ampClass],
  components: { ADotsResponsive, AVideo },
  props: {
    videos: propValidator([PROP_TYPES.ARRAY])
  },
  consts: {
    aspectRatios,
    FM_YOUTUBE_URL,
    mainVideoTitleSettings: {
      [breakpoints.desktopMd]: configADots(3, 22, false),
      [breakpoints.mobile]: configADots(3, 22, false)
    },
    mainVideoDescriptionSettings: {
      [breakpoints.desktopMd]: configADots(3, 22, false),
      [breakpoints.mobile]: configADots(5, 22, false)
    },
    otherVideoTitleSettings: {
      [breakpoints.aboveDesktopXl]: configADots(1, 20, false),
      [breakpoints.desktopLg]: configADots(1, 18, false),
      [breakpoints.mobile]: configADots(1, 22, false)
    },
    otherVideoDescriptionSettings: {
      [breakpoints.aboveDesktopXl]: configADots(3, 18, false),
      [breakpoints.desktopLg]: configADots(3, 15, false),
      [breakpoints.mobile]: configADots(2, 22, false)
    }
  },
  computed: {
    mainVideo() {
      return this.videos[0]
    },
    otherVideos() {
      return this.videos.slice(1, 5)
    }
  },
  methods: {
    getVideoTitle(video) {
      return video.Title || video.Video.Title
    },
    getVideoDescription(video) {
      return video.Video.Description
    }
  },
  mounted() {
    this.$emit('mounted')
  }
}
</script>

<style scoped lang="scss">
@import '~assets/scss/local/mixins/grid';
@import '~assets/scss/local/styles/article';

.featured-videos__wrapper {
  background: $c--black;
  $padding: (
    $above-desktop-xl: 60px 0,
    $desktop-md: 30px 0
  );

  @include setStyleByBreakpoint('padding', $padding, $handle-amp: true);

  &:not(#{$amp-selector}) {
    @include fullWidthWithoutPadding;
  }

  &#{$amp-selector} {
    @include setStyleByBreakpoint(
      'margin-left',
      negativeMapValues($amp-page-padding-left)
    );
    @include setStyleByBreakpoint(
      'margin-right',
      negativeMapValues($amp-page-padding-right)
    );
  }

  &:not(#{$amp-selector}) .featured-videos__layout-wrapper {
    @include page-layout;
  }

  .featured-videos__layout-wrapper {
    @include page-paddings($handle-amp: true);
  }

  .featured-videos__videos-wrapper {
    $margin-top: (
      $above-desktop-xl: 25px,
      $desktop-md: 0
    );
    @include setStyleByBreakpoint('margin-top', $margin-top, $handle-amp: true);

    $grid-setup: (
      $columnSetup: (
        $above-desktop-xl: 64.3% minmax(0, 100%),
        $desktop-md: minmax(0, 100%)
      ),
      $columnGapSetup: (
        $above-desktop-xl: 1.5%
      )
    );

    @include gridBuilder($grid-setup, $handle-amp: true);
  }

  .featured-videos__title {
    color: $c--white;

    @extend .section-title;
  }

  .featured-videos__main-text-wrapper {
    @include desktop-md {
      margin-top: 20px;
    }

    &#{$amp-selector} {
      margin-top: 20px;
    }
  }

  .featured-videos__main-video-title {
    /deep/ .dots__visible {
      color: $c--white;
    }

    @extend .main-video-title;
  }

  .featured-videos__main-video-description {
    margin-top: 20px;

    /deep/ .dots__visible {
      color: $c--white;
    }

    @extend .main-video-description;
  }

  .featured-videos__other-videos-wrapper {
    $grid-setup: (
      $columnSetup: (
        $above-desktop-xl: minmax(0, 100%),
        $desktop-md: repeat(2, minmax(0, 100%)),
        $mobile: minmax(0, 100%)
      ),
      $columnGapSetup: (
        $desktop-md: 60px
      ),
      $rowGapSetup: (
        $above-desktop-xl: 4.7%,
        $desktop-md: 20px
      )
    );

    @include gridBuilder($grid-setup, $handle-amp: true);

    $margin-top: (
      $desktop-md: 60px,
      $mobile: 20px
    );

    @include setStyleByBreakpoint('margin-top', $margin-top, $handle-amp: true);
  }

  .featured-videos__other-video-wrapper {
    $grid-setup: (
      $columnSetup: (
        $above-desktop-xl: 40% minmax(0, 100%),
        $desktop-md: minmax(0, 100%)
      ),
      $columnGapSetup: (
        $above-desktop-xl: 5%,
        $desktop-md: 20px
      )
    );

    @include gridBuilder($grid-setup, $handle-amp: true);
  }

  .featured-videos__text-wrapper {
    $margin-top: (
      $desktop-md: 20px
    );

    @include setStyleByBreakpoint('margin-top', $margin-top, $handle-amp: true);
  }

  .featured-videos__other-video-title {
    /deep/ .dots__visible {
      color: $c--white;

      @extend .other-video-title;
    }
  }

  .featured-videos__other-video-description {
    $margin-top: (
      $above-desktop-xl: 5px,
      $desktop-lg: 2px,
      $desktop-md: 10px
    );

    @include setStyleByBreakpoint('margin-top', $margin-top, $handle-amp: true);

    /deep/ .dots__visible {
      color: $c--white;

      @extend .other-video-description;
    }
  }

  .featured-videos__see-all-button {
    margin: 40px auto 0;
    text-transform: uppercase;
  }

  .featured-videos__more-videos {
    margin-top: 30px;
    color: $c--white;
    font-size: 18px;
    font-weight: 600;
  }
}
</style>
